import { useState, useEffect } from 'react';
import { Zap, TrendingUp, TrendingDown, Loader2, RefreshCw, Leaf, AlertCircle } from 'lucide-react';
import { getNEMPrices } from '../api/client';
import type { NEMPricesResponse, NEMRegionData } from '../types';

// Region display names
const REGION_NAMES: Record<string, string> = {
  NSW1: 'New South Wales',
  QLD1: 'Queensland',
  SA1: 'South Australia',
  TAS1: 'Tasmania',
  VIC1: 'Victoria',
};

// Region short names for compact display
const REGION_SHORT: Record<string, string> = {
  NSW1: 'NSW',
  QLD1: 'QLD',
  SA1: 'SA',
  TAS1: 'TAS',
  VIC1: 'VIC',
};

function formatPrice(pricePerKwh: number): string {
  // Convert $/kWh to c/kWh for display
  const cents = pricePerKwh * 100;
  if (Math.abs(cents) < 0.01) return '0.0c';
  return `${cents.toFixed(1)}c`;
}

function formatDemand(demandMw: number): string {
  if (demandMw >= 1000) {
    return `${(demandMw / 1000).toFixed(1)} GW`;
  }
  return `${demandMw.toFixed(0)} MW`;
}

function getPriceColor(pricePerKwh: number): string {
  const cents = pricePerKwh * 100;
  if (cents < 0) return 'text-green-600 dark:text-green-400'; // Negative pricing
  if (cents < 10) return 'text-green-600 dark:text-green-400';
  if (cents < 20) return 'text-yellow-600 dark:text-yellow-400';
  if (cents < 35) return 'text-orange-600 dark:text-orange-400';
  return 'text-red-600 dark:text-red-400';
}

function getPriceBg(pricePerKwh: number): string {
  const cents = pricePerKwh * 100;
  if (cents < 0) return 'bg-green-100 dark:bg-green-900/30';
  if (cents < 10) return 'bg-green-100 dark:bg-green-900/30';
  if (cents < 20) return 'bg-yellow-100 dark:bg-yellow-900/30';
  if (cents < 35) return 'bg-orange-100 dark:bg-orange-900/30';
  return 'bg-red-100 dark:bg-red-900/30';
}

interface RegionCardProps {
  data: NEMRegionData;
  isSelected?: boolean;
  onClick?: () => void;
}

function RegionCard({ data, isSelected, onClick }: RegionCardProps) {
  const priceColor = getPriceColor(data.price_kwh);
  const priceBg = getPriceBg(data.price_kwh);

  return (
    <button
      onClick={onClick}
      className={`w-full text-left p-3 rounded-lg border transition-all ${
        isSelected
          ? 'border-brand-orange bg-brand-orange/5'
          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
      }`}
    >
      <div className="flex items-center justify-between">
        <span className="font-medium text-gray-900 dark:text-white">
          {REGION_SHORT[data.region] || data.region}
        </span>
        <div className={`px-2 py-0.5 rounded ${priceBg}`}>
          <span className={`text-sm font-bold ${priceColor}`}>
            {formatPrice(data.price_kwh)}/kWh
          </span>
        </div>
      </div>
      <div className="mt-1 flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
        <span>{formatDemand(data.total_demand_mw)}</span>
        <span className="flex items-center gap-1">
          <Leaf className="w-3 h-3 text-green-500" />
          {data.renewable_percentage.toFixed(0)}%
        </span>
      </div>
    </button>
  );
}

export function NEMPricesWidget() {
  const [data, setData] = useState<NEMPricesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedRegion, setSelectedRegion] = useState<string>('NSW1');
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await getNEMPrices();
      setData(result);
      setLastUpdate(new Date());
    } catch (err) {
      setError('Failed to load NEM prices');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // Refresh every 5 minutes (AEMO data updates every 5 min)
    const interval = setInterval(loadData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const selectedData = data?.regions[selectedRegion];

  // Find highest and lowest priced regions
  const regions = data ? Object.values(data.regions) : [];
  const sortedByPrice = [...regions].sort((a, b) => a.price_kwh - b.price_kwh);
  const lowestRegion = sortedByPrice[0];
  const highestRegion = sortedByPrice[sortedByPrice.length - 1];

  return (
    <div className="card p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Zap className="w-5 h-5 text-brand-orange" />
          <h3 className="font-semibold text-gray-900 dark:text-white">NEM Spot Prices</h3>
          {data?.is_demo_data && (
            <span className="flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 rounded-full">
              <AlertCircle className="w-3 h-3" />
              Demo
            </span>
          )}
        </div>
        <button
          onClick={loadData}
          disabled={loading}
          className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-50"
          title="Refresh"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {error ? (
        <div className="text-center py-4 text-red-500 dark:text-red-400">{error}</div>
      ) : loading && !data ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 text-brand-orange animate-spin" />
        </div>
      ) : data ? (
        <>
          {/* Region Grid */}
          <div className="grid grid-cols-5 gap-2 mb-4">
            {['NSW1', 'QLD1', 'VIC1', 'SA1', 'TAS1'].map((region) => {
              const regionData = data.regions[region];
              if (!regionData) return null;
              return (
                <RegionCard
                  key={region}
                  data={regionData}
                  isSelected={selectedRegion === region}
                  onClick={() => setSelectedRegion(region)}
                />
              );
            })}
          </div>

          {/* Selected Region Details */}
          {selectedData && (
            <div className="p-4 bg-gray-50 dark:bg-gray-900 rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium text-gray-900 dark:text-white">
                  {REGION_NAMES[selectedData.region] || selectedData.region}
                </h4>
                <span className={`text-2xl font-bold ${getPriceColor(selectedData.price_kwh)}`}>
                  {formatPrice(selectedData.price_kwh)}/kWh
                </span>
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500 dark:text-gray-400">Spot Price</span>
                  <p className="font-medium text-gray-900 dark:text-white">
                    ${selectedData.price_mwh.toFixed(2)}/MWh
                  </p>
                </div>
                <div>
                  <span className="text-gray-500 dark:text-gray-400">Total Demand</span>
                  <p className="font-medium text-gray-900 dark:text-white">
                    {formatDemand(selectedData.total_demand_mw)}
                  </p>
                </div>
                <div>
                  <span className="text-gray-500 dark:text-gray-400">Scheduled Gen</span>
                  <p className="font-medium text-gray-900 dark:text-white">
                    {formatDemand(selectedData.scheduled_generation_mw)}
                  </p>
                </div>
                <div>
                  <span className="text-gray-500 dark:text-gray-400">Renewables</span>
                  <p className="font-medium text-green-600 dark:text-green-400">
                    {formatDemand(selectedData.semi_scheduled_generation_mw)} ({selectedData.renewable_percentage.toFixed(0)}%)
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Price Summary */}
          <div className="mt-4 flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
            <div className="flex items-center gap-4">
              {lowestRegion && (
                <span className="flex items-center gap-1">
                  <TrendingDown className="w-3 h-3 text-green-500" />
                  Lowest: {REGION_SHORT[lowestRegion.region]} {formatPrice(lowestRegion.price_kwh)}
                </span>
              )}
              {highestRegion && (
                <span className="flex items-center gap-1">
                  <TrendingUp className="w-3 h-3 text-red-500" />
                  Highest: {REGION_SHORT[highestRegion.region]} {formatPrice(highestRegion.price_kwh)}
                </span>
              )}
            </div>
            {lastUpdate && (
              <span>Updated {lastUpdate.toLocaleTimeString('en-AU')}</span>
            )}
          </div>
        </>
      ) : null}

      {/* Source attribution */}
      <div className="mt-3 text-xs text-gray-400 dark:text-gray-500 text-center">
        {data?.is_demo_data ? (
          <span className="text-amber-500 dark:text-amber-400">
            Demo data (AEMO API unavailable)
          </span>
        ) : (
          'Data source: AEMO National Electricity Market'
        )}
      </div>
    </div>
  );
}
